/**
 * <free-shipping-bar>
 * カート更新イベントを監視し、送料無料バナーの文言・進捗を動的切替える。
 * 閾値超過で data-state="achieved"、未達で data-state="progress"、空で "empty"。
 *
 * 依存: Atelier cart-drawer.js の CartUpdateEvent (publish() で発火)
 */

import { Component } from '@theme/component';

/**
 * @typedef {Object} CartSnapshot
 * @property {number} total_price - 合計金額 (cents, Shopifyは最小単位)
 * @property {number} item_count
 */

class FreeShippingBar extends Component {
  get threshold() {
    return parseInt(this.dataset.threshold ?? '20000', 10) || 20000;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('cart:update', this.handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:update', this.handleCartUpdate);
  }

  /**
   * @param {CustomEvent} event
   */
  handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;

    const cart = event.detail?.cart ?? event.detail?.resource ?? event.detail;
    if (!cart) return;

    if (typeof cart.total_price !== 'number') {
      this.fetchAndUpdate();
      return;
    }

    this.update(cart);
  };

  async fetchAndUpdate() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;
      const cart = await response.json();
      this.update(cart);
    } catch (_) {
      // Network failures should not block cart operations.
    }
  }

  /**
   * @param {CartSnapshot} cart
   */
  update(cart) {
    const totalJpy = Math.round((cart.total_price ?? 0) / 100);
    const threshold = this.threshold;
    const remaining = Math.max(threshold - totalJpy, 0);
    const percent = Math.min((totalJpy / threshold) * 100, 100);
    const isEmpty = (cart.item_count ?? 0) === 0;

    const state = isEmpty ? 'empty' : remaining > 0 ? 'progress' : 'achieved';
    this.dataset.state = state;

    const progressEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-progress]'));
    if (progressEl) {
      progressEl.style.setProperty('--pct', `${percent}%`);
    }

    this.renderMessage(state, { threshold, remaining });
  }

  /**
   * @param {'empty' | 'progress' | 'achieved'} state
   * @param {{ threshold: number, remaining: number }} params
   */
  renderMessage(state, { threshold, remaining }) {
    const textEl = this.querySelector('[data-text]');
    if (!textEl) return;

    const nf = new Intl.NumberFormat('ja-JP');

    // DOM APIs のみで構築し XSS リスクを排除
    while (textEl.firstChild) {
      textEl.removeChild(textEl.firstChild);
    }

    const strong = document.createElement('strong');

    switch (state) {
      case 'empty': {
        textEl.appendChild(document.createTextNode('合計'));
        strong.textContent = `¥${nf.format(threshold)}`;
        textEl.appendChild(strong);
        textEl.appendChild(document.createTextNode('以上で無料配送'));
        break;
      }
      case 'progress': {
        textEl.appendChild(document.createTextNode('あと'));
        strong.textContent = `¥${nf.format(remaining)}`;
        textEl.appendChild(strong);
        textEl.appendChild(document.createTextNode(' で無料配送の対象になります！'));
        break;
      }
      case 'achieved': {
        strong.textContent = '無料配送の対象になります！';
        textEl.appendChild(strong);
        break;
      }
    }
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
